import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import cc from 'classcat';
import { useSelector } from 'react-redux';
import { dispatch } from '../../store';
import {
    INavigationItem,
    selectMobileSideBar,
    updateMobileSideBarOpen,
    updateShowDropdown,
    closeAllDropdowns,
} from '../../store/slices/navigation/navigation.slice';
import { useTranslation } from '../../hooks/useTranslation';
import { Translation } from '../../commonTypes/translation.types';
import { PremiumTooltip } from '../Common/PremiumTooltip';
import { IBaseMenuItem } from '../../store/slices/navigation/navigation.slice';
import { useFeatureAvailability } from '../../hooks/features/useFeatureAvailability';

interface INavigationMenuItemProps extends INavigationItem {
    collapsed: boolean;
}

interface SubMenuItemProps {
    item: IBaseMenuItem;
    checkSelectedSubItem: (itemUrl: string, hasParams: boolean) => boolean;
    navigate: (url: string) => void;
    t: (key: keyof Translation) => string;
}

const SubMenuItem = ({ item, checkSelectedSubItem, navigate, t }: SubMenuItemProps) => {
    const mobileMenuOpen = useSelector(selectMobileSideBar);

    const featureAvailability = useFeatureAvailability(item.featureName || '');
    const isFeatureUnavailable = Boolean(item.featureName && !featureAvailability.isAvailable);

    const handleClick = () => {
        if (isFeatureUnavailable) return;
        mobileMenuOpen && dispatch(updateMobileSideBarOpen(false));
        navigate(item.url);
    };

    return (
        <li className={cc([isFeatureUnavailable && 'premium-tooltip-wrapper'])}>
            <button
                className={cc([
                    'menu-item__list-item',
                    checkSelectedSubItem(item.url, item.hasParams) && 'menu-item__list-item--active',
                    isFeatureUnavailable && 'menu-item__list-item--disabled',
                ])}
                onClick={handleClick}
                style={{ pointerEvents: isFeatureUnavailable ? 'none' : 'auto' }}
            >
                <p
                    className={cc([
                        'menu-item__list-item-label',
                        checkSelectedSubItem(item.url, item.hasParams) && 'menu-item__list-item-label--active',
                        isFeatureUnavailable && 'menu-item__list-item-label--disabled',
                    ])}
                >
                    {t(`navigation.${item.keyTitle}` as keyof Translation)}
                </p>
            </button>
            {isFeatureUnavailable && <PremiumTooltip position="top" showOnHover />}
        </li>
    );
};

const NavigationMenuItem = ({
    keyTitle,
    url,
    icon,
    activeIcon,
    iconAlt,
    subMenuItems,
    hasParams,
    disabled,
    collapsed,
    showDropdown,
    featureName,
}: INavigationMenuItemProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const mobileMenuOpen = useSelector(selectMobileSideBar);

    const [isActive, setIsActive] = useState<boolean>(false);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const [shouldRender, setShouldRender] = useState<boolean>(showDropdown ?? false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const featureAvailability = useFeatureAvailability(featureName || '');
    const isFeatureUnavailable = featureName && !featureAvailability.isAvailable;

    useEffect(() => {
        if (url) checkSelected(url, hasParams);

        if (collapsed && subMenuItems.length > 0) {
            const active = subMenuItems?.find(x => checkSelected(x.url, x.hasParams));
            if (active) setIsActive(true);
            else setIsActive(false);

            dispatch(updateShowDropdown({ keyTitle, showDropdown: false }));
        }
    }, [location.pathname]);

    useEffect(() => {
        if (collapsed && showDropdown) {
            setShouldRender(false);
            setIsAnimating(false);
        }
    }, [collapsed]);

    useEffect(() => {
        if (showDropdown) {
            setShouldRender(true);
            setIsAnimating(true);
        } else if (shouldRender) {
            setIsAnimating(false);
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [showDropdown]);

    const clickHandler = () => {
        if (disabled) return;
        if (isFeatureUnavailable) return;
        if (subMenuItems.length > 0) dispatch(updateShowDropdown({ keyTitle, showDropdown: !showDropdown }));
        else if (url) {
            dispatch(closeAllDropdowns());
            mobileMenuOpen && dispatch(updateMobileSideBarOpen(false));
            navigate(url);
        }
    };

    const checkSelected = (itemUrl: string, hasParams: boolean) => {
        if (subMenuItems && subMenuItems.length > 0) {
            for (let subItem of subMenuItems) {
                if (subItem.url === location.pathname) return true;
                if (location.pathname.startsWith(subItem.url + '/')) return true;
            }
        } else if (itemUrl) {
            if (hasParams && location.pathname.startsWith(itemUrl)) return true;
            return itemUrl === location.pathname;
        }
    };

    const checkSelectedSubItem = (itemUrl: string, hasParams: boolean) => {
        if (hasParams && location.pathname.startsWith(itemUrl)) return true;
        if (itemUrl === location.pathname) return true;
        if (location.pathname.startsWith(itemUrl + '/')) return true;
        return false;
    };

    return (
        <div className={cc(['menu-item', collapsed && 'menu-item__collapsed'])}>
            <div className={cc([isFeatureUnavailable && 'premium-tooltip-wrapper'])}>
                <button
                    ref={buttonRef}
                    disabled={disabled}
                    className={cc([
                        'menu-item__button',
                        { 'menu-item--active': isActive || showDropdown },
                        { 'menu-item--opened': checkSelected(url, hasParams) },
                        { 'menu-item--disabled': disabled || isFeatureUnavailable },
                    ])}
                    onClick={clickHandler}
                >
                    <div className="menu-item__label-icon">
                        <img src={checkSelected(url, hasParams) || isActive || showDropdown ? activeIcon : icon} alt={iconAlt} />
                        <p
                            className={`${
                                checkSelected(url, hasParams) || isActive || showDropdown ? 'menu-item__label--active' : 'menu-item__label'
                            } paragraph-4`}
                        >
                            {t(`navigation.${keyTitle}` as keyof Translation)}
                        </p>
                    </div>
                    {subMenuItems.length > 0 && (
                        <img
                            className={cc(['menu-item__dropdown--icon', { 'menu-item__dropdown--icon--up': showDropdown }])}
                            src="/svg/sidebar/arrow-down.svg"
                            alt="Dropdown down Arrow"
                        />
                    )}
                </button>
                {isFeatureUnavailable && <PremiumTooltip position="right" showOnHover />}
            </div>
            {shouldRender && (
                <>
                    <div
                        className={cc([
                            'menu-item__dropdown',
                            { 'menu-item__dropdown--entering': isAnimating && !collapsed },
                            { 'menu-item__dropdown--exiting': !isAnimating && !collapsed },
                        ])}
                        style={{ top: buttonRef.current?.getBoundingClientRect().top }}
                    >
                        <ul className="menu-item__dropdown--list">
                            {subMenuItems.map(item => (
                                <SubMenuItem key={item.url} item={item} checkSelectedSubItem={checkSelectedSubItem} navigate={navigate} t={t} />
                            ))}
                        </ul>
                    </div>
                    {collapsed && (
                        <div
                            className="menu-item__event-bubbling-mask"
                            onClick={() => dispatch(updateShowDropdown({ keyTitle, showDropdown: false }))}
                        ></div>
                    )}
                </>
            )}
        </div>
    );
};

export default NavigationMenuItem;
